import React from "react";
import styled from "styled-components";
import { Project } from "../types";
import {
  Container,
  Section,
  SectionTitle,
  Card,
  Grid,
  Button,
} from "../styles/GlobalStyles";
import { useLanguage } from '../contexts/LanguageContext';

const ProjectsGrid = styled(Grid)`
  margin-bottom: 3rem;
`;

const ProjectCard = styled(Card)`
  overflow: hidden;
  position: relative;
`;

const ProjectImage = styled.div<{ $imageUrl?: string }>`
  height: 250px;
  background: ${(props) =>
    props.$imageUrl
      ? `url(${props.$imageUrl})`
      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"};
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  ${ProjectCard}:hover &::after {
    opacity: 1;
  }
`;

const ProjectContent = styled.div`
  padding: 2rem;
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ProjectDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const TechnologyList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TechnologyTag = styled.span`
  background: #f1f3f5;
  color: #667eea;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const LinkButton = styled(Button)`
  padding: 8px 20px;
  font-size: 0.9rem;
`;

interface ProjectsProps {
  projects: Project[];
}

const Projects: React.FC<ProjectsProps> = ({ projects }) => {
  const { t } = useLanguage();
  
  return (
    <Section id="projects">
      <Container>
        <SectionTitle>{t('projects.title')}</SectionTitle>

        <ProjectsGrid>
          {projects.map((project) => (
            <ProjectCard key={project.id}>
              <ProjectImage $imageUrl={project.imageUrl}>
                {!project.imageUrl && project.title.charAt(0)}
              </ProjectImage>

              <ProjectContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>

                <TechnologyList>
                  {project.technologies.map((tech, index) => (
                    <TechnologyTag key={index}>{tech}</TechnologyTag>
                  ))}
                </TechnologyList>

                <ProjectLinks>
                  {project.githubUrl && (
                    <LinkButton
                      as="a"
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="secondary"
                    >
                      {t('projects.viewCode')}
                    </LinkButton>
                  )}
                  {project.liveUrl && (
                    <LinkButton
                      as="a"
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="primary"
                    >
                      {t('projects.viewDemo')}
                    </LinkButton>
                  )}
                </ProjectLinks>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </Container>
    </Section>
  );
};

export default Projects;
